import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSession } from "../lib/authClient";
import { useMe } from "../lib/hooks";

/**
 * Session guard for the authenticated app shell.
 *
 * Gated behind `VITE_REQUIRE_AUTH` so the seeded demo stays clickable without
 * login (the default): when the flag is off this is a pass-through.
 *
 * When the flag is on:
 *   - no session            → redirect to /login (preserving the target).
 *   - session, unverified   → redirect to /verify-email (defense-in-depth for
 *                             the Login-flow gate; catches a direct /app hit).
 *   - session, has org      → render the app.
 *   - session, no org yet   → resume the "/start" funnel (workspace step).
 *
 * The server auto-provisions an org on first authed request, so the "no org"
 * branch is mainly the safety net for a hardened (PRO_DEMO_FALLBACK=false)
 * deployment and lets a first-time user name their workspace.
 */
const REQUIRE_AUTH = import.meta.env.VITE_REQUIRE_AUTH === "true";

export function AuthGate({ children }: { children: ReactNode }) {
  if (!REQUIRE_AUTH) return <>{children}</>;
  return <Guarded>{children}</Guarded>;
}

function Guarded({ children }: { children: ReactNode }) {
  const location = useLocation();
  const session = useSession();
  // Only resolve membership/org once we know there's a session, to avoid a
  // spurious /me fetch (and its auto-provision) while signed out.
  const me = useMe();

  if (session.isPending) return <FullscreenSpinner />;

  if (!session.data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  // Signed in — wait for /me, then decide verify vs onboarding vs app.
  if (me.isPending) return <FullscreenSpinner />;
  if (me.data?.user && !me.data.user.emailVerified) {
    const email = encodeURIComponent(me.data.user.email);
    return <Navigate to={`/verify-email?email=${email}`} replace />;
  }
  // Signed in but no workspace → resume the full-screen onboarding funnel,
  // which starts at the workspace step (account already exists) and lands them
  // back in the app once the org is created.
  if (me.data && !me.data.activeOrg) {
    return <Navigate to="/start" replace />;
  }

  return <>{children}</>;
}

function FullscreenSpinner() {
  const { t } = useTranslation();
  return (
    <div className="grid h-full place-items-center bg-surface-0">
      <div
        className="h-6 w-6 animate-spin rounded-full border-2 border-line border-t-brand"
        role="status"
        aria-label={t("common.loading")}
      />
    </div>
  );
}
